/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { type RefObject } from 'react';
import { type IntentConfiguration } from '@stripe/stripe-react-native';
import { isToday } from 'date-fns';
import { type ContextFrom, createMachine, send } from 'xstate';
import { assign } from 'xstate/lib/actions';
import {
  getRoundedPercentageOfValue,
  getRoundedValueOfPercentage,
} from '@sg/garnish';
import { OrderPaymentStatus } from '@sg/graphql-schema';

import { ignoreTimezone } from '@order/utils';

import { sumLedgerTotal } from '../components';
import {
  adjustWantedTime,
  currencyCode,
  getInitialWantedTime,
  getMaxTipAmount,
  googlePayConfig,
  isOrderPaymentStatePending,
  merchantDisplayName,
  returnURL,
  setupFutureUsage,
  storeWantedTime,
  stripeAppearance,
  walletConfig,
} from '../utils';
import {
  type BagReward,
  type OrderingCart,
  type OrderingLedger,
  type OrderingMachineActions,
  type OrderingMachineContext,
  type OrderingMachineEvents,
  type OrderingMachineServices,
  type PaymentMethod,
  type WantedTime,
} from './ordering-machine.types';
import { orderingMachineLogger } from './ordering-machine.utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A state machine to manage the ordering flow on bag + checkout.
 */
export const createOrderingMachine = <LineItem, DeliveryOrderDetail>(
  params: CreateOrderingMachineParams,
) => {
  const {
    orderSubmissionDelay,
    wantedTimesPollingInterval,
    paymentStatePollingInterval,
    paymentStateExpirationTolerance,
    isIntentConfirmationEnabled,
    isPaymentStatePollingEnabled,
    stripeConfirmHandlerRef,
  } = params;

  return createMachine(
    {
      // eslint-disable-next-line @typescript-eslint/consistent-type-imports
      tsTypes: {} as import('./ordering-machine.typegen').Typegen0,
      predictableActionArguments: true,
      preserveActionOrder: true,
      id: 'ordering-state-machine',
      schema: {
        context: {} as OrderingMachineContext<LineItem, DeliveryOrderDetail>,
        events: {} as OrderingMachineEvents,
        services: {} as OrderingMachineServices<LineItem, DeliveryOrderDetail>,
        actions: {} as OrderingMachineActions<LineItem, DeliveryOrderDetail>,
      },
      context: getInitialContext<LineItem, DeliveryOrderDetail>(
        isPaymentStatePollingEnabled,
      ),
      type: 'parallel',
      states: {
        ordering: {
          initial: 'idle',
          states: {
            idle: {
              initial: 'waiting',
              states: {
                waiting: {
                  description: 'An empty state used for the initial setup.',
                },
                postOrder: {
                  id: 'post-order',
                  entry: ['storeOrderPostSubmission'],
                  after: {
                    1500: {
                      actions: [
                        'handlePostOrderPurchaseModal',
                        'handlePostOrderNotifications',
                        'handlePostOrderTracking',
                        'handlePostOrderHistory',
                        'handlePostOrderAppReview',
                        'navigatePostOrder',
                        'resetContext',
                        'clearLocalStorageOrderId',
                      ],
                    },
                  },
                },
                idle: {
                  id: 'idle',
                  description: 'An idle state that awaits the next event.',
                  on: {
                    FETCH_INITIAL_DATA: {
                      description: 'Fetches the initial data.',
                      target: '#fetching-initial-data',
                    },
                    ADD_UPSELL_TO_BAG: {
                      description: 'Adds an upsell to the bag.',
                      target: '#adding-upsell-to-bag',
                      actions: ['setUpsellBeingAdded'],
                    },
                    CHANGE_LINE_ITEM_QUANTITY: {
                      description: 'Changes a line item quantity.',
                      target: '#changing-line-item-quantity',
                      actions: ['setLineItemQuantity'],
                    },
                    CHANGE_UTENSILS_USAGE: {
                      description: 'Changes the utensils usage',
                      actions: ['changeUtensilsUsage'],
                    },
                    CHANGE_DROPOFF_LOCATION: {
                      description: 'Changes the dropoff location',
                      actions: ['changeDropoffLocation'],
                    },
                    SHOW_LOYALTY_INFO: {
                      description: 'Shows the loyalty availability info.',
                      target: '#loyalty-info',
                    },
                    SHOW_LOYALTY_TERMS: {
                      description: 'Shows the loyalty terms.',
                      actions: ['setRewardTermsBeingShown'],
                      target: '#loyalty-terms',
                    },
                    APPLY_REWARD: {
                      description: 'Applies a reward.',
                      actions: ['setRewardIdBeingUpdated'],
                      target: '#applying-reward',
                    },
                    REMOVE_REWARD: {
                      description: 'Removes a reward.',
                      actions: ['setRewardIdBeingUpdated'],
                      target: '#removing-reward',
                    },
                    APPLY_PROMO_CODE: {
                      description: 'Applies a promo code.',
                      target: '#applying-promo-code',
                    },
                    REQUEST_CHANNEL_LOCATION_CHANGE: {
                      description:
                        'Requests the location or channel to be changed',
                      actions: ['changeChannelOrLocation'],
                    },
                    REQUEST_TIME_CHANGE: [
                      {
                        target: '#changing-time',
                        cond: 'checkIfLoggedIn',
                      },
                      {
                        actions: ['requestLogin'],
                      },
                    ],
                    REQUEST_DELIVERY_PREFERENCES_CHANGE: {
                      description:
                        'Requests the delivery preferences to be changed',
                      target: '#changing-delivery-preferences',
                    },
                    REQUEST_CHECKOUT_LEDGER: {
                      description: 'Requests the checkout ledger',
                      target: '#checkout-ledger',
                    },
                  },
                },
                changingTime: {
                  id: 'changing-time',
                  on: {
                    CANCEL_REQUEST: '#idle',
                    CHANGE_TIME: [
                      {
                        description: 'Changes the time',
                        actions: ['changeTime', 'storeWantedTime'],
                        target: '#idle',
                        cond: 'checkIfDoesNotNeedsToShowFutureDateModal',
                      },
                      {
                        description:
                          'Changes the time and shows future date warning',
                        actions: ['changeTime', 'storeWantedTime'],
                        target: '#showing-future-date-warning',
                        cond: 'checkIfNeedsToShowFutureDateModal',
                      },
                    ],
                  },
                },
                changingDeliveryPreferences: {
                  id: 'changing-delivery-preferences',
                  on: {
                    CANCEL_REQUEST: '#idle',
                    CHANGE_DELIVERY_PREFERENCES: {
                      actions: ['changeDeliveryPreferences'],
                      target: '#idle',
                    },
                  },
                },
                showingFutureDateWarning: {
                  id: 'showing-future-date-warning',
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
                showingRewardWarning: {
                  id: 'showing-reward-warning',
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
                checkoutLedger: {
                  id: 'checkout-ledger',
                  on: {
                    CANCEL_REQUEST: '#idle',
                    CHANGE_TIP: { actions: ['changeTip'] },
                    REQUEST_PAYMENT_METHOD_CHANGE: '#changing-payment-method',
                    CHANGE_CREDIT_USAGE: { actions: ['changeCreditUsage'] },
                  },
                },
                postOrderFailureWithNextDayOrderNotice: {
                  id: 'post-order-failure-with-next-day-order-notice',
                  after: {
                    100: '#next-day-order-notice',
                  },
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
                timeslotAdjustedNotice: {
                  id: 'timeslot-adjusted-notice',
                  on: {
                    CANCEL_REQUEST: '#idle',
                    REQUEST_TIME_CHANGE: '#changing-time',
                    SUBMIT_ORDER: '#submitting-order',
                  },
                },
                nextDayOrderNotice: {
                  id: 'next-day-order-notice',
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
                orderPlayback: {
                  id: 'order-playback',
                  after: {
                    ORDER_SUBMISSION_DELAY: [
                      {
                        target: '#confirming-stripe',
                        cond: 'checkIfShouldConfirmIntent',
                        // If confirmation is enabled, it must confirm the payment intent.
                        actions: ['confirmStripe'],
                      },
                      {
                        // When it is disabled, it must submit the order.
                        actions: [send('SUBMIT_ORDER')], // "send" avoids ts issue with delayed transitions, while "raise" does not work.
                      },
                    ],
                  },
                  on: {
                    CANCEL_REQUEST: {
                      target: ['#idle', '#order-payment-state-idle'],
                      actions: [
                        'incrementPurchaseAttempt',
                        'clearLocalStorageOrderId',
                      ],
                    },
                  },
                },
                changingPaymentMethod: {
                  id: 'changing-payment-method',
                  on: {
                    CANCEL_REQUEST: '#idle',
                    REQUEST_CHECKOUT_LEDGER: '#checkout-ledger',
                    REQUEST_GIFT_CARD_FORM: '#gift-card-form',
                    FINISH_PAYMENT_SELECTION: '#checkout-ledger',
                    CHANGE_PAYMENT_METHOD: {
                      description: 'Changes the payment method',
                      actions: ['changePaymentMethod'],
                    },
                  },
                },
                giftCardForm: {
                  id: 'gift-card-form',
                  on: {
                    CANCEL_REQUEST: '#idle',
                    REQUEST_PAYMENT_METHOD_CHANGE: '#changing-payment-method',
                    APPLY_GIFT_CARD: '#applying-gift-card',
                  },
                },
                loyaltyInfo: {
                  id: 'loyalty-info',
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
                loyaltyTerms: {
                  id: 'loyalty-terms',
                  on: {
                    CANCEL_REQUEST: '#idle',
                  },
                },
              },
            },
            loading: {
              states: {
                fetchingInitialData: {
                  id: 'fetching-initial-data',
                  initial: 'fetching',
                  states: {
                    fetching: {
                      description: `A state for fetching the initial data (cart + rewards).`,
                      invoke: {
                        src: 'fetchInitialData',
                        onDone: {
                          target: '#retrieving-initial-wanted-time',
                          actions: ['onFetchInitialDataDone', 'onBagReady'],
                        },
                      },
                    },
                  },
                },
                retrievingInitialWantedTime: {
                  id: 'retrieving-initial-wanted-time',
                  initial: 'retrieving',
                  states: {
                    retrieving: {
                      description:
                        'A state for retrieving the initial wanted time from local storage, if it exists.',
                      invoke: {
                        src: 'getInitialWantedTime',
                        onDone: [
                          {
                            target: '#retrieving-initial-order-payment-state',
                            actions: ['onGetInitialWantedTimeDone'],
                            cond: 'checkIfPaymentStatePollingEnabled',
                          },
                          {
                            target: '#idle',
                            actions: ['onGetInitialWantedTimeDone'],
                          },
                        ],
                        onError: '#idle',
                      },
                    },
                  },
                },
                retrievingInitialOrderPaymentState: {
                  id: 'retrieving-initial-order-payment-state',
                  initial: 'retrieving',
                  states: {
                    retrieving: {
                      invoke: {
                        src: 'fetchOrderPaymentState',
                        onDone: {
                          target: ['#idle', '#order-payment-state-checking'],
                          actions: ['onOrderPaymentStateDone'],
                        },
                        onError: '#idle',
                      },
                    },
                  },
                },
                fetchingStatePostOrder: {
                  id: 'fetching-state-post-order',
                  invoke: {
                    src: 'fetchStatePostOrder',
                    onDone: {
                      target: '#post-order',
                      actions: ['onFetchStatePostOrderDone'],
                    },
                    onError: '#post-order',
                  },
                },
                addingUpsellToBag: {
                  id: 'adding-upsell-to-bag',
                  initial: 'adding',
                  states: {
                    adding: {
                      description: 'A state for adding an upsell to the bag.',
                      invoke: {
                        src: 'addUpsellToBag',
                        onDone: {
                          target: ['#idle', '#preparing-to-fetch-rewards'],
                          actions: ['onAddUpsellToBagDone'],
                        },
                      },
                    },
                  },
                },
                changingLineItemQuantity: {
                  id: 'changing-line-item-quantity',
                  initial: 'changing',
                  states: {
                    changing: {
                      description:
                        'A state for changing the line item quantity.',
                      invoke: {
                        src: 'updateLineItem',
                        onDone: {
                          target: ['#idle', '#preparing-to-fetch-rewards'],
                          actions: ['onUpdateLineItemDone'],
                        },
                      },
                      on: {
                        CHANGE_LINE_ITEM_QUANTITY: {
                          description:
                            'Changes another line item quantity while the present one is being changed.',
                          target: '#changing-line-item-quantity',
                          actions: ['setLineItemQuantity'],
                        },
                      },
                    },
                  },
                },
                applyingReward: {
                  id: 'applying-reward',
                  initial: 'fetching',
                  states: {
                    fetching: {
                      description: 'A state for applying the reward.',
                      invoke: {
                        src: 'applyReward',
                        onDone: [
                          {
                            target: ['#idle', '#fetching-rewards'],
                            actions: ['onApplyRewardDone'],
                            cond: (_context, event) => 'cart' in event.data,
                          },
                          {
                            target: '#showing-reward-warning',
                            actions: ['onRewardError'],
                            cond: (_context, event) => 'error' in event.data,
                          },
                        ],
                      },
                    },
                  },
                },
                removingReward: {
                  id: 'removing-reward',
                  initial: 'fetching',
                  states: {
                    fetching: {
                      description: 'A state for removing the reward.',
                      invoke: {
                        src: 'removeReward',
                        onDone: [
                          {
                            target: ['#idle', '#fetching-rewards'],
                            actions: ['onRemoveRewardDone'],
                            cond: (_context, event) => 'cart' in event.data,
                          },
                          {
                            target: '#showing-reward-warning',
                            actions: ['onRewardError'],
                            cond: (_context, event) => 'error' in event.data,
                          },
                        ],
                      },
                    },
                  },
                },
                applyingPromoCode: {
                  id: 'applying-promo-code',
                  initial: 'applying',
                  states: {
                    applying: {
                      description: 'A state for applying the promo code.',
                      invoke: {
                        src: 'applyPromoCode',
                        onDone: [
                          {
                            target: '#idle',
                            actions: ['onApplyPromoCodeDone'],
                            cond: (_context, event) => event.data.success,
                          },
                          {
                            target: '#showing-reward-warning',
                            actions: ['onRewardError'],
                            cond: (_context, event) => !event.data.success,
                          },
                        ],
                      },
                    },
                  },
                },
                applyingGiftCard: {
                  id: 'applying-gift-card',
                  initial: 'applying',
                  states: {
                    applying: {
                      description: 'A state for applying a gift card.',
                      invoke: {
                        src: 'applyGiftCard',
                        onDone: [
                          {
                            target: '#changing-payment-method',
                            actions: ['onApplyGiftCardDone'],
                            cond: 'checkIfGiftCardApplied',
                          },
                          {
                            target: '#gift-card-form',
                            cond: 'checkIfGiftCardNotApplied',
                          },
                        ],
                      },
                    },
                  },
                },
                confirmingStripe: {
                  id: 'confirming-stripe',
                  on: {
                    CANCEL_REQUEST: {
                      target: ['#idle', '#order-payment-state-idle'],
                      actions: ['clearLocalStorageOrderId'],
                    },
                  },
                },
                submittingOrder: {
                  id: 'submitting-order',
                  initial: 'submitting',
                  states: {
                    submitting: {
                      description: `A state for submitting the order`,
                      on: {
                        CANCEL_REQUEST: {
                          target: ['#idle', '#order-payment-state-idle'],
                          actions: ['clearLocalStorageOrderId'],
                        },
                      },
                      invoke: {
                        src: 'submitOrder',
                        onError: {
                          target: '#idle',
                          actions: ['incrementPurchaseAttempt'],
                        },
                        onDone: [
                          {
                            target: '#post-order',
                            cond: 'checkIfOrderSubmissionSuccess',
                          },
                          {
                            target:
                              '#post-order-failure-with-next-day-order-notice',
                            actions: [
                              'handleTimeslotUnavailable',
                              'incrementPurchaseAttempt',
                            ],
                            cond: 'checkIfWillBeFutureOrder',
                          },
                          {
                            target: '#timeslot-adjusted-notice',
                            actions: [
                              'handleTimeslotUnavailable',
                              'incrementPurchaseAttempt',
                            ],
                            cond: 'checkIfTimeslotIsUnavailable',
                          },
                          {
                            target: '#idle',
                            actions: ['incrementPurchaseAttempt'],
                            cond: 'checkIfGenericOrderSubmissionError',
                          },
                        ],
                      },
                    },
                  },
                },
              },
            },
          },
        },
        orderPaymentState: {
          initial: 'idle',
          states: {
            idle: {
              id: 'order-payment-state-idle',
            },
            error: {
              id: 'order-payment-state-error',
            },
            polling: {
              id: 'order-payment-state-polling',
              invoke: {
                src: 'fetchOrderPaymentState',
                onDone: {
                  target: '#order-payment-state-checking',
                  actions: ['onOrderPaymentStateDone'],
                },
                onError: {
                  target: ['#idle', '#order-payment-state-error'],
                  actions: ['clearLocalStorageOrderId'],
                },
              },
            },
            waiting: {
              id: 'order-payment-state-waiting',
              after: {
                PAYMENT_STATE_POLLING: '#order-payment-state-polling',
              },
            },
            checking: {
              id: 'order-payment-state-checking',
              always: [
                {
                  target: [
                    '#fetching-state-post-order',
                    '#order-payment-state-idle',
                  ],
                  cond: 'checkIfOrderPaymentStateFulfilled',
                },
                {
                  target: ['#idle', '#order-payment-state-error'],
                  actions: [
                    'onOrderPaymentStateError',
                    'clearLocalStorageOrderId',
                    'incrementPurchaseAttempt',
                  ],
                  cond: 'checkIfOrderPaymentStateFailed',
                },
                {
                  target: '#order-payment-state-waiting',
                  cond: 'checkIfOrderPaymentIsPending',
                },
                {
                  target: ['#idle', '#order-payment-state-idle'],
                  actions: [
                    'clearLocalStorageOrderId',
                    'incrementPurchaseAttempt',
                  ],
                },
              ],
            },
          },
        },
        wantedTimes: {
          initial: 'waiting',
          states: {
            polling: {
              invoke: {
                src: 'fetchWantedTimes',
                onDone: {
                  target: 'waiting',
                  actions: ['updateWantedTimes'],
                  cond: 'checkIfHasWantedTimes',
                },
              },
            },
            waiting: {
              on: { REFETCH_WANTED_TIMES: 'polling' },
              after: {
                [wantedTimesPollingInterval]: {
                  target: 'polling',
                },
              },
            },
          },
        },
        lineItems: {
          initial: 'idle',
          states: {
            idle: {
              id: 'line-items-idle',
              on: {
                START_CHANGING_LINE_ITEM_QUANTITY: {
                  description:
                    'When the customer taps a quantity stepper button but the request as not started yet, we still need to block proceeding to checkout.',
                  target: '#line-items-pending-quantity-change',
                },
              },
            },
            pending: {
              id: 'line-items-pending-quantity-change',
              on: {
                STOP_CHANGING_LINE_ITEM_QUANTITY: {
                  description:
                    'When the customer cancels the quantity stepper change by returning to the original quantity, or when the quantity change request starts.',
                  target: '#line-items-idle',
                },
              },
            },
          },
        },
        rewards: {
          initial: 'idle',
          states: {
            idle: {
              id: 'rewards-idle',
              on: {
                ASSIGN_REWARDS: {
                  actions: ['updateRewards'],
                },
                REWARDS_FETCH_ERROR: {
                  target: '#rewards-error',
                  actions: ['clearRewards', 'onFetchRewardsError'],
                },
              },
            },
            error: {
              id: 'rewards-error',
              on: {
                REFETCH_REWARDS: '#preparing-to-fetch-rewards',
              },
            },
            preparingToFetchRewards: {
              id: 'preparing-to-fetch-rewards',
              always: [
                {
                  target: '#fetching-rewards-deferred',
                  cond: 'checkIfShouldDeferRewardsFetching',
                },
                {
                  target: '#fetching-rewards',
                  cond: 'checkIfLoggedIn',
                },
                {
                  target: '#rewards-idle',
                },
              ],
            },
            fetchingRewards: {
              id: 'fetching-rewards',
              invoke: {
                src: 'fetchRewards',
                onDone: {
                  target: '#rewards-idle',
                  actions: ['updateRewards'],
                },
                onError: {
                  target: '#rewards-error',
                  actions: ['onFetchRewardsError'],
                },
              },
            },
            fetchingRewardsDeferred: {
              id: 'fetching-rewards-deferred',
              description:
                'Deferred version of the `fetchingRewards` state that allows to change the context and state using external events rather than an inline service.',
              on: {
                ASSIGN_REWARDS: {
                  target: '#rewards-idle',
                  actions: ['updateRewards'],
                },
                REWARDS_FETCH_ERROR: {
                  target: '#rewards-error',
                  actions: ['clearRewards', 'onFetchRewardsError'],
                },
              },
            },
          },
        },
      },
      on: {
        START: {
          description:
            'Starts/restarts the machine by fetching the initial data and clearing the state.',
          target: ['#fetching-initial-data', '#preparing-to-fetch-rewards'],
          actions: ['resetContext'],
        },
        CHANGE_PAYMENT_METHOD: {
          description: 'Changes the payment method',
          actions: ['changePaymentMethod'],
        },
        SET_CONFIRM_STRIPE: {
          description: 'Sets the confirm stripe action',
          actions: ['setConfirmStripe'],
        },
        CANCEL_REQUEST: {
          target: ['#idle', '#order-payment-state-idle'],
          actions: ['clearLocalStorageOrderId'],
        },
        START_STRIPE_CONFIRMATION: {
          description: 'Toggles the stripe confirmation indicator',
          target: '#confirming-stripe',
          actions: ['setHasTriggeredOrderSubmission'],
        },
        SHOW_ORDER_PLAYBACK: {
          target: '#order-playback',
          actions: ['changePaymentMethod', 'setHasTriggeredOrderSubmission'],
        },
        SUBMIT_ORDER: [
          {
            target: ['#idle', '#order-payment-state-polling'],
            actions: ['changePaymentMethod', 'setHasTriggeredOrderSubmission'],
            cond: 'checkIfPaymentStatePollingEnabled',
          },
          {
            target: '#submitting-order',
            actions: ['changePaymentMethod', 'setHasTriggeredOrderSubmission'],
          },
        ],
      },
    },
    {
      actions: {
        /**
         * Stores the resolved initial data in the context.
         */
        onFetchInitialDataDone: assign({
          isInitialDataReady: true,
          isWantedTimeAsap: true,

          orderId: (_context, event) => event.data.cart.orderId,
          cart: (_context, event) => event.data.cart,
          paymentMethods: (_context, event) => event.data.paymentMethods,
          defaultPaymentMethodId: (_context, event) =>
            event.data.defaultPaymentMethodId,
          hasDefaultPaymentMethodCard: (_context, event) =>
            event.data.paymentMethods.some(
              (pm) => pm.id === event.data.defaultPaymentMethodId,
            ),
          selectedTip: (_context, event) => event.data.selectedTip,
          availableTips: (_context, event) => event.data.availableTips,
          availablePoints: (_context, event) => event.data.availablePoints,
          availableCredit: (_context, event) => event.data.availableCredit,
          giftCardBalance: (_context, event) => event.data.giftCardBalance,
          customer: (_context, event) => event.data,
          wantedTime: (_context, event) => event.data.cart.wantedTimes[0],
          dropoffLocation: (_context, event) =>
            event.data.cart.dropoffLocations[0]?.value,
          appliedRewardId(_context, event) {
            return event.data.cart.appliedRewardId;
          },
          stripeCustomerId: (_context, event) => event.data.stripeCustomerId,
          stripeCustomerKey: (_context, event) => event.data.stripeCustomerKey,
          stripeSession: (_context, event) => event.data.stripeSession,
          stripeIntentConfig: (_context, event) => {
            const { cart, selectedTip, availableCredit } = event.data;
            const { total } = sumLedgerTotal(cart.ledger, selectedTip, availableCredit, true); // prettier-ignore
            const amount = total <= 0 ? 100 : total;

            return {
              confirmHandler: async (
                paymentResult,
                shouldSavePaymentMethod,
                intentCreationCallback,
              ) => {
                stripeConfirmHandlerRef.current(
                  paymentResult,
                  shouldSavePaymentMethod,
                  intentCreationCallback,
                );
              },
              paymentMethodTypes: isPaymentStatePollingEnabled
                ? ['card', 'cashapp']
                : ['card'],
              mode: isIntentConfirmationEnabled
                ? { amount, currencyCode }
                : { amount, currencyCode, setupFutureUsage },
              paymentMethodOptions: {
                setupFutureUsageValues: {
                  card: 'OffSession',
                },
              },
            };
          },
          stripeElementConfig: (_context, event) => ({
            merchantDisplayName,
            returnURL,
            applePay: walletConfig,
            googlePay: googlePayConfig,
            customerId: event.data.stripeCustomerId,
            customerEphemeralKeySecret: event.data.stripeCustomerKey,
            appearance: stripeAppearance,
          }),
        }),

        onOrderPaymentStateDone: assign({
          paymentStateExpiration: (_context, event) => event.data.paymentStateExpiration, // prettier-ignore
          paymentStateStatus: (_context, event) => event.data.paymentStateStatus, // prettier-ignore
          paymentStateError: (_context, event) => event.data.paymentStateError, // prettier-ignore
        }),

        onOrderPaymentStateError(_context) {
          orderingMachineLogger.debug(
            'Please provide `onOrderPaymentStateError` action.',
          );
        },

        onFetchStatePostOrderDone: assign({
          orderId: (_context, event) => event.data.order.id,
          hasEarnedLoyaltyPoints: (_context, event) => event.data.order.hasEarnedLoyaltyPoints, // prettier-ignore
          cart: (_context, event) => event.data.cart,
        }),

        setConfirmStripe: assign({
          confirmStripe: (_context, event) => event.params.confirmStripe,
        }),

        setHasTriggeredOrderSubmission: assign({
          hasTriggeredOrderSubmission: true,
        }),

        onGetInitialWantedTimeDone: assign({
          wantedTime: (_context, event) => event.data.wantedTime,
          isWantedTimeAsap: (context, event) =>
            context.cart.wantedTimes[0] === event.data.wantedTime,
        }),

        /**
         * Stores the rewards that were returned.
         */
        updateRewards: assign({
          rewards: (_context, event) => event.data.rewards,
          appliedRewardId(context, event) {
            if (!context.appliedRewardId) return undefined;

            // discard the applied reward if it's no longer present.
            return event.data.rewards.find(
              (reward) => reward.id === context.appliedRewardId,
            )?.id;
          },
        }),

        /**
         * Clears rewards.
         */
        clearRewards: assign({
          rewards: [],
          appliedRewardId: undefined,
        }),

        /**
         * Logs errors with fetching rewards.
         */
        onFetchRewardsError() {
          orderingMachineLogger.error('Failed to fetch rewards');
        },

        /**
         * Stores which upsell is being added.
         */
        setUpsellBeingAdded: assign({
          lineItemIdsBeingUpdated: (context, event) => [
            ...context.lineItemIdsBeingUpdated,
            event.params.productId,
          ],
        }),

        /**
         * Stores which line item quantity is being changed.
         */
        setLineItemQuantity: assign({
          lineItemIdsBeingUpdated: (context, event) => [
            ...context.lineItemIdsBeingUpdated,
            event.params.lineItemId,
          ],
        }),

        /**
         * Updates the resolved cart in the context.
         */
        onAddUpsellToBagDone: assign({
          lineItemIdsBeingUpdated: [],
          cart(context, event) {
            if ('cart' in event.data) {
              return event.data.cart;
            }

            return context.cart;
          },
        }),

        /**
         * Updates the resolved cart in the context.
         * It also updates the tip to be the same percentage, but of the new subtotal.
         */
        onUpdateLineItemDone: assign({
          selectedTip: (context, event) =>
            getNewTip(
              context.selectedTip,
              context.cart.maxTipAmount,
              context.availableTips,
              event.data?.ledger,
            ),
          cart: (context, event) => event?.data ?? context.cart,
          appliedRewardId(_context, event) {
            return event.data?.appliedRewardId;
          },
          lineItemIdsBeingUpdated: [],
        }),

        /**
         * Stores which reward is being updated.
         */
        setRewardIdBeingUpdated: assign({
          rewardIdBeingUpdated: (_context, event) => event.params.rewardId,
        }),

        /**
         * Stores which terms are being shown.
         */
        setRewardTermsBeingShown: assign({
          rewardTermsBeingShown: (_context, event) => event.params.rewardId,
        }),

        /**
         * Stores the updated cart in the context.
         */
        onApplyRewardDone: assign({
          rewardIdBeingUpdated: undefined,
          appliedRewardId(_context, event) {
            if ('cart' in event.data) {
              return event.data?.cart?.appliedRewardId;
            }
          },
          cart(context, event) {
            if ('cart' in event.data) {
              return event.data.cart;
            }

            return context.cart;
          },
          selectedTip: (context, event) => {
            if ('cart' in event.data) {
              return getNewTip(
                context.selectedTip,
                context.cart.maxTipAmount,
                context.availableTips,
                event.data?.cart?.ledger,
              );
            }

            return context.selectedTip;
          },
        }),

        /**
         * Stores the updated cart in the context.
         */
        onRemoveRewardDone: assign({
          rewardIdBeingUpdated: undefined,
          appliedRewardId: undefined,
          cart(context, event) {
            if ('cart' in event.data) {
              return event.data.cart;
            }

            return context.cart;
          },
          selectedTip: (context, event) => {
            if ('cart' in event.data) {
              return getNewTip(
                context.selectedTip,
                context.cart.maxTipAmount,
                context.availableTips,
                event.data?.cart?.ledger,
              );
            }

            return context.selectedTip;
          },
        }),

        /**
         * Stores the reward error in the context.
         */
        onRewardError: assign({
          rewardIdBeingUpdated: undefined,
          rewardError(_context, event) {
            if ('error' in event.data) {
              return event.data.error;
            }
          },
          isRewardMissingSku(_context, event) {
            if ('isRewardMissingSku' in event.data) {
              return Boolean(event.data.isRewardMissingSku);
            }

            return false;
          },
        }),

        /**
         * Stores the resulting rewards post promo code in the context.
         */
        onApplyPromoCodeDone: assign({
          rewards: (_context, event) => event.data.rewards,
        }),

        /**
         * Stores the resulting gift card balance and available credit in the context.
         */
        onApplyGiftCardDone: assign({
          giftCardBalance: (context, event) =>
            event.data?.giftCardBalance ?? context.giftCardBalance,
          availableCredit: (context, event) =>
            event.data?.availableCredit ?? context.availableCredit,
        }),

        /**
         * Resets the context of the machine.
         *
         * Can be used to clear the state before restarting the machine.
         */
        resetContext: assign(
          getInitialContext<LineItem, DeliveryOrderDetail>(
            isPaymentStatePollingEnabled,
          ),
        ),

        /**
         * Changes the channel or location.
         */
        changeChannelOrLocation(_context) {
          orderingMachineLogger.debug(
            'Please provide `changeChannelOrLocation` action.',
          );
        },

        /**
         * Changes the time.
         */
        changeTime: assign({
          wantedTime: (_context, event) => event.params.wantedTime,
          isWantedTimeAsap: (context, event) =>
            context.cart.wantedTimes[0] === event.params.wantedTime,
          hasShownFutureDateWarning: (context, event) =>
            event.params.isFutureDate || context.hasShownFutureDateWarning,
        }),

        async storeWantedTime(context, event) {
          if (!context?.cart?.orderId || !event?.params?.wantedTime?.time) {
            return;
          }

          await storeWantedTime(
            context.cart.orderId,
            event.params.wantedTime.time,
          );
        },

        /**
         * Changes the delivery preferences.
         */
        changeDeliveryPreferences: assign({
          cart: (context, event) => ({
            ...context.cart,
            deliveryPreferences: event.params,
          }),
        }),

        /**
         * Updates the wanted times.
         */
        updateWantedTimes: assign({
          cart: (context, event) => ({
            ...context.cart,
            wantedTimes: event.data.wantedTimes,
          }),
          wantedTime(context, event) {
            const firstAvailableTimeslot = event.data.wantedTimes[0];

            // Do not overwrite the selected timeslot with the first one.
            if (!context.isWantedTimeAsap) {
              return context.wantedTime;
            }

            // Do not automatically bump the order to a day in the future.
            if (!isWantedTimeToday(firstAvailableTimeslot)) {
              return context.wantedTime;
            }

            return firstAvailableTimeslot;
          },
        }),

        /**
         * Changes the tip.
         */
        changeTip: assign({
          selectedTip: (_context, event) => event.params.tip,
        }),

        /**
         * Changes the dropoff location.
         */
        changeDropoffLocation: assign({
          dropoffLocation: (_context, event) => event.params.dropoffLocation,
        }),

        /**
         * Changes the payment method id for this order.
         */
        changePaymentMethod: assign({
          paymentMethodId: (context, event) =>
            event?.params?.paymentMethodId || context.paymentMethodId,
        }),

        /**
         * Changes whether or not this order should use credit.
         */
        changeCreditUsage: assign({
          shouldUseCredit: (_context, event) => event.params.shouldUseCredit,
        }),

        /**
         * Changes the payment method id and whether credit is used.
         */
        changeUtensilsUsage: assign({
          shouldIncludeUtensils: (_context, event) =>
            event.params.shouldIncludeUtensils,
        }),

        /**
         * If the order fails due to timeslot unavailable, select the next available time.
         * Also removes the failed timeslot from the available times.
         */
        handleTimeslotUnavailable: assign({
          cart: (context) => ({
            ...context.cart,
            wantedTimes: context.cart.wantedTimes.filter(
              (wantedTime) => wantedTime !== context.wantedTime,
            ),
          }),
          previousWantedTime: (context) => context.wantedTime,
          wantedTime(context) {
            const nextTimeslot = context.cart.wantedTimes.find(
              (wantedTime) => wantedTime !== context.wantedTime,
            );

            // Do not automatically bump the order to a day in the future.
            if (!isWantedTimeToday(nextTimeslot)) {
              return context.wantedTime;
            }

            return nextTimeslot;
          },
          isWantedTimeAsap: true,
        }),

        /**
         * Redirects the customer to the login screen.
         */
        requestLogin(_context) {
          orderingMachineLogger.debug('Please provide `requestLogin` action.');
        },

        /**
         * Stores the order post submission in the context for delayed post-order actions.
         */
        storeOrderPostSubmission: assign({
          orderId: (context, event) => {
            const data = event?.data as {
              order?: { id?: string };
            };

            return data?.order?.id ?? context.orderId;
          },
          hasEarnedLoyaltyPoints: (context, event) => {
            const data = event?.data as {
              order?: { hasEarnedLoyaltyPoints?: boolean };
            };

            return (
              data?.order?.hasEarnedLoyaltyPoints ??
              context.hasEarnedLoyaltyPoints
            );
          },
        }),

        // Used to remount the Stripe element after failures.
        incrementPurchaseAttempt: assign({
          purchaseAttempt: (context) => context.purchaseAttempt + 1,
        }),

        async handlePostOrderPurchaseModal(_context) {
          orderingMachineLogger.debug(
            'Please provide `handlePostOrderPurchaseModal` action.',
          );

          return undefined;
        },

        async handlePostOrderNotifications(_context) {
          orderingMachineLogger.debug(
            'Please provide `handlePostOrderNotifications` action.',
          );

          return undefined;
        },

        async handlePostOrderTracking(_context) {
          orderingMachineLogger.debug(
            'Please provide `handlePostOrderTracking` action.',
          );

          return undefined;
        },

        async handlePostOrderHistory(_context) {
          orderingMachineLogger.debug(
            'Please provide `handlePostOrderHistory` action.',
          );

          return undefined;
        },

        async handlePostOrderAppReview(_context) {
          orderingMachineLogger.debug(
            'Please provide `handlePostOrderAppReview` action.',
          );

          return undefined;
        },

        clearLocalStorageOrderId(_context) {
          orderingMachineLogger.debug(
            'Please provide `clearLocalStorageOrderId` action.',
          );

          return undefined;
        },

        confirmStripe(context) {
          context.confirmStripe();
        },

        navigatePostOrder(_context) {
          orderingMachineLogger.debug(
            'Please provide `navigatePostOrder` action.',
          );
        },

        onBagReady(_context) {
          orderingMachineLogger.debug('Please provide `onBagReady` action.');
        },
      },
      services: {
        async fetchInitialData(_context) {
          orderingMachineLogger.debug('Please provide `fetchCart` service.');

          return {
            cart: {} as OrderingCart<LineItem, DeliveryOrderDetail>,
            paymentMethods: [] as readonly PaymentMethod[],
            defaultPaymentMethodId: undefined,
            hasDefaultPaymentMethodCard: false,
            selectedTip: 0,
            availableTips: [],
            availablePoints: 0,
            availableCredit: 0,
            giftCardBalance: 0,
            customerId: '',
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            stripeCustomerId: '',
            stripeCustomerKey: '',
            stripeSession: '',
            isNewCustomer: true,
            isOptedOutFromLoyalty: false,
          };
        },
        async fetchOrderPaymentState(_context) {
          orderingMachineLogger.debug(
            'Please provide `fetchOrderPaymentState` service.',
          );

          return {
            order: { id: '', hasEarnedLoyaltyPoints: false },
            paymentStateExpiration: undefined,
            paymentStateStatus: undefined,
            paymentStateError: undefined,
          };
        },
        async fetchStatePostOrder(_context) {
          orderingMachineLogger.debug(
            'Please provide `fetchStatePostOrder` service.',
          );

          return {
            order: { id: '', hasEarnedLoyaltyPoints: false },
            cart: {} as OrderingCart<LineItem, DeliveryOrderDetail>,
          };
        },
        async getInitialWantedTime(context) {
          return {
            wantedTime: await getInitialWantedTime(
              context.cart.orderId,
              context.cart.wantedTimes,
            ),
          };
        },
        async fetchRewards(_context) {
          orderingMachineLogger.debug('Please provide `fetchRewards` service.');

          return {
            rewards: [] as readonly BagReward[],
          };
        },
        async fetchWantedTimes(_context) {
          orderingMachineLogger.debug(
            'Please provide `fetchWantedTimes` service.',
          );

          return {
            wantedTimes: [],
          };
        },
        async addUpsellToBag(_context) {
          orderingMachineLogger.debug(
            'Please provide `addUpsellToBag` service.',
          );

          return { cart: {} as OrderingCart<LineItem, DeliveryOrderDetail> };
        },
        async updateLineItem(_context) {
          orderingMachineLogger.debug(
            'Please provide `updateLineItem` service.',
          );

          return {} as OrderingCart<LineItem, DeliveryOrderDetail>;
        },
        async applyReward(_context) {
          orderingMachineLogger.debug('Please provide `applyReward` service.');

          return { cart: {} as OrderingCart<LineItem, DeliveryOrderDetail> };
        },
        async removeReward(_context) {
          orderingMachineLogger.debug('Please provide `removeReward` service.');

          return { cart: {} as OrderingCart<LineItem, DeliveryOrderDetail> };
        },
        async applyPromoCode(_context) {
          orderingMachineLogger.debug(
            'Please provide `applyPromoCode` service.',
          );

          return {
            rewards: [],
            success: false,
            error: undefined,
            isRewardMissingSku: false,
          };
        },
        async applyGiftCard(_context) {
          orderingMachineLogger.debug(
            'Please provide `applyGiftCard` service.',
          );

          return null;
        },
        async submitOrder(_context) {
          orderingMachineLogger.debug('Please provide `submitOrder` service.');

          return {
            success: false,
            isTimeslotUnavailable: false,
            error: undefined,
          };
        },
      },
      guards: {
        checkIfDoesNotNeedsToShowFutureDateModal: (context, event) =>
          !event.params.isFutureDate || context.hasShownFutureDateWarning,
        checkIfNeedsToShowFutureDateModal: (context, event) =>
          event.params.isFutureDate && !context.hasShownFutureDateWarning,
        checkIfOrderSubmissionSuccess: (_context, event) => event.data.success,
        checkIfTimeslotIsUnavailable: (_context, event) =>
          !event.data.success && event.data.isTimeslotUnavailable,
        checkIfWillBeFutureOrder(context, event) {
          if (event.data.success || !event.data.isTimeslotUnavailable) {
            return false;
          }

          const nextTimeslot = context.cart.wantedTimes.find(
            (wantedTime) => wantedTime !== context.wantedTime,
          );

          return !isWantedTimeToday(nextTimeslot);
        },
        checkIfGenericOrderSubmissionError: (_context, event) =>
          !event.data.success && !event.data.isTimeslotUnavailable,
        checkIfGiftCardApplied: (_context, event) =>
          Boolean(event.data?.giftCardBalance),
        checkIfGiftCardNotApplied: (_context, event) =>
          !event.data?.giftCardBalance,
        checkIfHasWantedTimes: (_context, event) =>
          event.data.wantedTimes.length > 0,
        checkIfLoggedIn() {
          orderingMachineLogger.debug(
            'Please provide `checkIfLoggedIn` guard.',
          );

          return false;
        },
        checkIfShouldDeferRewardsFetching() {
          orderingMachineLogger.debug(
            'Please provide `checkIfShouldDeferRewardsFetching` guard.',
          );

          return false;
        },
        checkIfShouldConfirmIntent() {
          orderingMachineLogger.debug(`Will confirm intent: ${isIntentConfirmationEnabled}`); // prettier-ignore

          return isIntentConfirmationEnabled;
        },
        checkIfPaymentStatePollingEnabled(context, event) {
          const willPollForPaymentState = isPaymentStatePollingEnabled && !fallbackToPaymentMethodSubmission(context, event); // prettier-ignore
          orderingMachineLogger.debug(`Will poll for payment state: ${willPollForPaymentState}`); // prettier-ignore

          return willPollForPaymentState;
        },
        checkIfOrderPaymentIsPending(context) {
          const { paymentStateStatus, paymentStateExpiration } = context;

          const willFetchPaymentState = isOrderPaymentStatePending(paymentStateStatus, paymentStateExpiration, paymentStateExpirationTolerance); // prettier-ignore
          orderingMachineLogger.debug(`Will fetch payment state: ${willFetchPaymentState}`); // prettier-ignore

          return willFetchPaymentState;
        },
        checkIfOrderPaymentStateFulfilled(context) {
          const isFulfilled = context.paymentStateStatus === OrderPaymentStatus.Fulfilled; // prettier-ignore
          orderingMachineLogger.debug(`Will move to post order: ${isFulfilled}`); // prettier-ignore

          return isFulfilled;
        },
        checkIfOrderPaymentStateFailed(context) {
          const isFailed = context.paymentStateStatus === OrderPaymentStatus.Failed || Boolean(context.paymentStateError); // prettier-ignore
          orderingMachineLogger.debug(`Will move to error: ${isFailed}`); // prettier-ignore

          return isFailed;
        },
      },
      delays: {
        ORDER_SUBMISSION_DELAY: () => orderSubmissionDelay,
        PAYMENT_STATE_POLLING: () => paymentStatePollingInterval,
      },
    },
  );
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const getInitialContext = <LineItem, DeliveryOrderDetail>(
  isPaymentStatePollingEnabled: boolean,
): OrderingMachineContext<LineItem, DeliveryOrderDetail> => {
  return {
    isInitialDataReady: false,
    cart: {} as OrderingCart<LineItem, DeliveryOrderDetail>,
    rewards: [],
    rewardError: undefined,
    isRewardMissingSku: false,
    appliedRewardId: undefined,
    paymentMethods: [],
    defaultPaymentMethodId: undefined,
    hasDefaultPaymentMethodCard: false,
    availablePoints: 0,
    availableCredit: 0,
    giftCardBalance: 0,

    purchaseAttempt: 0,
    stripeCustomerId: '',
    stripeCustomerKey: '',
    stripeSession: '',
    stripeElementConfig: null,
    stripeIntentConfig: null,
    confirmStripe: () =>
      Promise.resolve({ status: 'canceled', error: undefined }),

    hasTriggeredOrderSubmission: false,
    isPaymentStatePollingEnabled,
    paymentStateExpiration: undefined,
    paymentStateStatus: undefined,
    paymentStateError: undefined,

    customer: {
      customerId: '',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      isNewCustomer: true,
      isOptedOutFromLoyalty: false,
    },

    wantedTime: undefined,
    previousWantedTime: undefined,
    isWantedTimeAsap: true,
    hasShownFutureDateWarning: false,
    isShowingOrderPlayback: false,
    dropoffLocation: undefined,
    paymentMethodId: undefined,
    shouldUseCredit: true,
    shouldIncludeUtensils: false,

    selectedTip: 0,
    availableTips: [],

    lineItemIdsBeingUpdated: [],
    rewardIdBeingUpdated: undefined,
    rewardTermsBeingShown: undefined,

    // Store order data from the original event for delayed post-order actions
    orderId: undefined,
    hasEarnedLoyaltyPoints: undefined,
  };
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const isWantedTimeToday = (time?: WantedTime) => {
  const adjustedTime = adjustWantedTime(time);
  const validTime = adjustedTime ? ignoreTimezone(adjustedTime) : null;

  return Boolean(validTime && isToday(validTime));
};

/**
 * Adjusts the tip to be the same percentage, but of the new subtotal.
 */
const getNewTip = (
  previousTip: number,
  previousMaxTipAmount: number,
  availableTips: ReadonlyArray<number | 'CUSTOM'>,
  newLedger?: OrderingLedger,
): number => {
  const previousPercentage = getRoundedPercentageOfValue(
    previousTip,
    previousMaxTipAmount,
  );

  // Pre-defined tips, keep the same percentage of the new subtotal.
  if (availableTips.includes(previousPercentage)) {
    const newMaximumTip = getMaxTipAmount(newLedger);
    return getRoundedValueOfPercentage(previousPercentage, newMaximumTip) || 0;
  }

  // Custom tip over new maximum tip, set it to the max allowed.
  const newMaximumTip = getMaxTipAmount(newLedger);
  if (previousTip > newMaximumTip) {
    return newMaximumTip;
  }

  // Custom tip under new maximum tip, remain as is.
  return previousTip;
};

const fallbackToPaymentMethodSubmission = (
  context: ReturnType<typeof getInitialContext>,
  event: Record<string, unknown>,
) => {
  // If there is a wanted time key, then the check is happening post redirect.
  const isPostRedirect = 'wantedTime' in ((event as any)?.data ?? {});

  // If the order was already fulfilled, then the total will be zero but we still wish to poll.
  const wasAlreadyFulfilled = context.cart.lineItems.length === 0;

  // There are edge cases where the Stripe SDK fails and we fallback to a saved payment method.
  const paymentMethodId = (event as any)?.params?.paymentMethodId || context.paymentMethodId; // prettier-ignore
  const isPaymentMethodSelected = paymentMethodId?.startsWith('pm_');

  const { total } = sumLedgerTotal(
    context.cart.ledger,
    context.selectedTip,
    context.availableCredit,
    context.shouldUseCredit && !isPostRedirect,
  );

  // If there is no amount to charge, we fallback to a saved payment method.
  const hasZeroTotal = total <= 0 && !wasAlreadyFulfilled;

  return isPaymentMethodSelected || hasZeroTotal;
};

// ─── Types ──────────────────────────────────────────────────────────────────

export type OrderingContext<PartialLineItem, DeliveryOrderDetail> = ContextFrom<
  ReturnType<typeof createOrderingMachine<PartialLineItem, DeliveryOrderDetail>>
>;

export type CreateOrderingMachineParams = {
  orderSubmissionDelay: number;
  wantedTimesPollingInterval: number;
  paymentStatePollingInterval: number;
  paymentStateExpirationTolerance: number;
  isIntentConfirmationEnabled: boolean;
  isPaymentStatePollingEnabled: boolean;
  stripeConfirmHandlerRef: RefObject<
    NonNullable<IntentConfiguration['confirmHandler']>
  >;
};
